import { useEffect, useRef, useState } from 'react'
import {
  countdown, destinationOf, ferry, clock, originOf, ROUTES, type LastChance, type Sailing,
} from '../lib/ferry'
import { t } from '../lib/i18n'
import RouteMap from './RouteMap'
import Icon, { type IconName } from './Icon'

/** Part of the day a sailing leaves in, by the PRINTED clock in Puerto Rico. */
type Part = 'morning' | 'afternoon' | 'evening'
const PARTS: Part[] = ['morning', 'afternoon', 'evening']
const hourInPR = (ms: number) =>
  Number(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone: 'America/Puerto_Rico' }).format(ms))
const partOf = (ms: number): Part => {
  const h = hourInPR(ms)
  return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening'
}

/*
 * The rider's route, remembered.
 *
 * Most people on this service cross the same water every time, so the four
 * destination buttons were a question asked on every visit with the same
 * answer. Once picked it collapses to one line with a Change button. Per
 * device, in localStorage — a convenience, never a setting anything depends on.
 */
const ROUTE_KEY = 'ferry.route'
const savedRoute = () => { try { return localStorage.getItem(ROUTE_KEY) ?? '' } catch { return '' } }
const saveRoute = (r: string) => {
  try { if (r) localStorage.setItem(ROUTE_KEY, r); else localStorage.removeItem(ROUTE_KEY) } catch { /* private mode */ }
}

const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Puerto_Rico' }).format(new Date())

const KIND: Record<string, { icon: IconName; label: () => string }> = {
  passenger: { icon: 'people', label: () => t('sail.peopleOnly') },
  mixed: { icon: 'car', label: () => t('sail.carsAndPeople') },
  cargo: { icon: 'cargo', label: () => t('sail.cargo') },
}

/**
 * The board.
 *
 * The screen somebody opens standing on the dock, and it answers the questions
 * asked there in the order they are asked: where am I going, is there still a
 * boat, and how long have I got.
 */
export default function Sailings({ onBook, canBook }: { onBook: (s: Sailing) => void; canBook: boolean }) {
  const [date, setDate] = useState(today())
  const [route, setRouteState] = useState(() => {
    const r = savedRoute()
    return ROUTES.some((x) => x.key === r) ? r : ''
  })
  const [picking, setPicking] = useState(route === '')
  const setRoute = (r: string) => { setRouteState(r); saveRoute(r); setPicking(r === '') }
  const [sailings, setSailings] = useState<Sailing[]>([])
  const [lastChance, setLastChance] = useState<LastChance[]>([])
  const [closedRoutes, setClosedRoutes] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  /*
   * Re-fetched on a timer, not because the schedule changes but because the
   * CLOCK does. A page left open across a cutoff would otherwise keep offering a
   * Book button for a sailing the server has already stopped selling — the
   * button would work right up until it returned a 409, which is the worst way
   * to find out.
   */
  useEffect(() => {
    let live = true
    const load = () => {
      ferry.sailings(date, route || undefined)
        .then((d) => {
          if (!live) return
          setSailings(d.sailings); setLastChance(d.lastChance); setClosedRoutes(d.closedRoutes); setError('')
        })
        .catch((e: Error) => live && setError(e.message))
        .finally(() => live && setLoading(false))
    }
    setLoading(true); load()
    const timer = window.setInterval(load, 60_000)
    return () => { live = false; window.clearInterval(timer) }
  }, [date, route])

  const isToday = date === today()
  const upcoming = sailings.filter((s) => !s.departed)
  const gone = sailings.filter((s) => s.departed)

  /*
   * One part of the day open at a time.
   *
   * The default is the part holding the NEXT boat: on today's board that is
   * the part of the day it is now, and on a future date it is the first part
   * with anything in it. The list is upcoming-only and in departure order, so
   * both cases are just "the first upcoming sailing's part".
   *
   * Once the rider opens or closes a part themselves, the minute refresh must
   * not undo it, so it follows the default only until they touch it. A new
   * date or route is a new question and goes back to the default.
   */
  const firstPart: Part | null = upcoming[0] ? partOf(upcoming[0].scheduled_departure) : null
  const [openPart, setOpenPart] = useState<Part | null>(null)
  const riderChose = useRef(false)
  useEffect(() => { riderChose.current = false }, [date, route])
  useEffect(() => {
    const present = (p: Part | null) => p !== null && upcoming.some((x) => partOf(x.scheduled_departure) === p)
    setOpenPart((current) => {
      if (!riderChose.current) return firstPart
      // Their part emptied as its last boat left: move on rather than show nothing.
      if (current !== null && !present(current)) return firstPart
      return current
    })
    // `upcoming` is derived from `sailings`; firstPart captures what matters of it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstPart, sailings, date, route])
  const toggle = (part: Part) => {
    riderChose.current = true
    setOpenPart((current) => (current === part ? null : part))
  }

  return (
    <section className="card">
      <h1>{t('sail.title')}</h1>

      {/*
        * WHERE IT IS GOING, first and largest.
        *
        * This was a select labelled "Route" reading "All routes", which is the
        * system's word for a thing nobody thinks in. People think in
        * destinations, so the destinations are the control: full-width targets
        * naming the place, with the direction drawn rather than described.
        */}
      {route && !picking ? (
        <div className="route-chosen">
          <span className="route-chosen-name">
            <Icon name="ferry" size={18} />
            <strong>{originOf(route)} <Icon name="chevron" size={14} /> {destinationOf(route)}</strong>
          </span>
          <button className="link" onClick={() => setPicking(true)}>{t('sail.changeRoute')}</button>
        </div>
      ) : (
        <div className="destinations" role="group" aria-label={t('sail.route')}>
          <button className={route === '' ? 'on' : ''} onClick={() => setRoute('')}>
            <span className="dest-all">{t('sail.allRoutes')}</span>
          </button>
          {ROUTES.map((r) => (
            <button key={r.key} className={route === r.key ? 'on' : ''} onClick={() => setRoute(r.key)}>
              <span className="dest-from">{originOf(r.key)}</span>
              <Icon name="chevron" size={14} />
              <span className="dest-to">{destinationOf(r.key)}</span>
            </button>
          ))}
        </div>
      )}

      <div className="filters">
        <label>
          <span className="muted small">{t('sail.date')}</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
      </div>

      {/* The anxious question on an island, answered before anything else. */}
      {isToday && lastChance.map((l) => <LastChanceBanner key={l.route} l={l} />)}
      {isToday && closedRoutes.map((r) => (
        <p key={r} className="notice bad last-chance-none">
          <Icon name="alert" size={16} />
          {t('sail.noneLeft')} {destinationOf(r)}.
        </p>
      ))}

      {loading && <p className="muted">{t('app.loading')}</p>}
      {error && <p className="error">{error}</p>}
      {!loading && !error && sailings.length === 0 && <p className="muted">{t('sail.none')}</p>}

      {/* Morning, afternoon, evening: how people actually plan a crossing ("an
          early boat", "something after lunch"), rather than one long run of
          times to read top to bottom. Empty parts are left out. */}
      {PARTS.map((part) => {
        const these = upcoming.filter((x) => partOf(x.scheduled_departure) === part)
        if (!these.length) return null
        const open = openPart === part
        return (
          <div key={part} className={`day-part${open ? ' open' : ''}`}>
            <h3 className="day-part-title">
              <button
                className="day-part-head"
                onClick={() => toggle(part)}
                aria-expanded={open}
                aria-controls={`day-part-${part}`}
              >
                <Icon name={part === 'morning' ? 'sunrise' : part === 'afternoon' ? 'clock' : 'moon'} size={16} />
                {t(`sail.${part}`)}
                <span className="muted small">{these.length}</span>
                <Icon name="chevron" size={16} className={`col-caret${open ? ' open' : ''}`} />
              </button>
            </h3>
            <ul className="sailings" id={`day-part-${part}`} hidden={!open}>
              {these.map((s) => <SailingRow key={s.id} s={s} onBook={onBook} canBook={canBook} />)}
            </ul>
          </div>
        )
      })}

      {/*
        * Departed sailings are shown, dimmed, rather than hidden. Somebody
        * reading today's board is also learning the pattern of the service, and
        * a list that silently starts at 3pm hides half of it. Collapsed, because
        * they are history and should not be the first thing on the screen.
        */}
      {gone.length > 0 && (
        <details className="gone">
          <summary>{gone.length} {t('sail.alreadyGone')}</summary>
          <ul className="sailings">
            {gone.map((s) => <SailingRow key={s.id} s={s} onBook={onBook} canBook={canBook} />)}
          </ul>
        </details>
      )}
    </section>
  )
}

function LastChanceBanner({ l }: { l: LastChance }) {
  // Ticks locally so the countdown is live without re-fetching the list.
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(timer)
  }, [])
  const minutes = Math.round((l.closesAt - now) / 60000)
  const urgent = minutes <= 90

  return (
    <div className={`last-chance${urgent ? ' urgent' : ''}`}>
      <Icon name={urgent ? 'alert' : 'clock'} size={20} />
      <div>
        {/*
          * The ORIGIN as well as the destination.
          *
          * Two routes end at Ceiba — from Vieques and from Culebra — so a banner
          * saying only "Last ferry to Ceiba" rendered twice with two different
          * times and no way to tell which was which. They are genuinely different
          * facts, not a duplicate to collapse.
          */}
        <strong>
          {t('sail.lastTo')} {originOf(l.route)} <Icon name="chevron" size={13} /> {destinationOf(l.route)}
          {' · '}{clock(l.departsAt)}
        </strong>
        <p>
          {minutes > 0
            ? <>{t('sail.bookingCloses')} <b>{countdown(minutes)}</b> · {l.vesselName}</>
            : t('sail.bookingClosed')}
        </p>
      </div>
    </div>
  )
}

function SailingRow({ s, onBook, canBook }: { s: Sailing; onBook: (s: Sailing) => void; canBook: boolean }) {
  const [open, setOpen] = useState(false)
  const kind = KIND[s.vessel_kind] ?? KIND.passenger!
  // Closing within the hour is worth saying out loud on the row itself.
  const closingSoon = s.bookable && s.minutesToClose <= 60

  return (
    <li className={`sailing status-${s.status}${s.departed ? ' is-gone' : ''}`}>
      <button className="sailing-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="col-time">
          <strong>{clock(s.scheduled_departure)}</strong>
          {s.delayMinutes ? <em className="late">{s.delayMinutes} {t('sail.late')}</em> : null}
        </span>

        <span className="col-vessel">
          {/* The direction, on every row. Reading a time without a destination is
              how somebody boards the boat going the wrong way. */}
          <strong className="col-route">
            {originOf(s.route)} <Icon name="chevron" size={13} /> {destinationOf(s.route)}
          </strong>
          <em className="kind">
            <Icon name={kind.icon} size={14} />{s.vessel_name} · {kind.label()}
          </em>
        </span>

        <span className="col-seats">
          {/* Cargo is checked before everything else: "Full" or "Closed" on a boat
              nobody can board reads as bad luck rather than as the rule it is. */}
          {s.restricted ? <em className="warn-text">{s.restricted === 'unconfirmed' ? t('sail.unconfirmed') : t('sail.cargoOnly')}</em>
            : s.status === 'cancelled' ? <em className="bad">{t('sail.cancelled')}</em>
            : s.departed ? <em className="muted">{s.status === 'arrived' ? t('sail.arrived') : t('sail.gone')}</em>
            : s.seatsLeft <= 0 ? <em className="bad">{t('sail.full')}</em>
            : !s.bookable ? <em className="warn-text">{t('sail.closed')}</em>
            : <>
                <strong>{s.seatsLeft}</strong>
                <em className={closingSoon ? 'warn-text' : 'muted'}>
                  {closingSoon ? countdown(s.minutesToClose) : t('sail.seatsLeft')}
                </em>
              </>}
        </span>

        <Icon name="chevron" size={16} className={`col-caret${open ? ' open' : ''}`} />
      </button>

      {open && (
        <div className="sailing-detail">
          <RouteMap sailing={s} />
          {s.restricted === 'hazmat' && <p className="notice bad">{t('sail.hazmat')}</p>}
          {s.restricted === 'cargo' && <p className="notice bad">{t('sail.cargoNotice')}</p>}
          {s.restricted === 'unconfirmed' && <p className="notice bad">{t('sail.unconfirmedNotice')}</p>}
          {/*
            * A passenger holding a ticket that says 4:45 is looking at a row that
            * says 3:45. Without this line the app looks wrong; with it, it is the
            * only place they will be told. The footnote itself comes straight
            * from the operator's PDF, in their words.
            */}
          {s.movedByFootnote && s.printed_departure != null && (
            <p className="notice">
              {t('sail.printedAs')} <b>{clock(s.printed_departure)}</b>
              {s.schedule_note && <> · {s.schedule_note}</>}
            </p>
          )}
          {!s.movedByFootnote && s.schedule_note && !s.restricted && (
            <p className="muted small">{s.schedule_note}</p>
          )}
          {s.status_note && <p className="notice">{s.status_note}</p>}
          <p className="muted small">
            {s.booked} / {s.passenger_capacity} {t('sail.booked')}
            {s.vehicle_capacity ? ` · ${s.vehicle_capacity} ${t('book.vehicle').toLowerCase()}` : ''}
          </p>

          {s.bookable && canBook && (
            <>
              <button className="primary with-icon" onClick={() => onBook(s)}>
                <Icon name="ticket" size={18} />{t('sail.book')}
              </button>
              <p className="muted small">
                {t('sail.closesAt')} {clock(s.closesAt)}
                {s.vessel_kind !== 'passenger' && <> · {t('sail.vehiclesBy')} {clock(s.vehicleClosesAt)}</>}
              </p>
            </>
          )}
          {s.bookable && !canBook && <p className="muted small">{t('sail.signInToBook')}</p>}
          {/*
            * A closed sailing says WHY rather than simply lacking a button. An
            * absent control is indistinguishable from a broken one.
            */}
          {!s.bookable && (
            <p className="notice bad">
              <Icon name="info" size={16} />
              {s.departed ? t('sail.alreadyLeft')
                : s.status === 'cancelled' ? t('sail.wasCancelled')
                : s.seatsLeft <= 0 ? t('sail.soldOut')
                : `${t('sail.closedAt')} ${clock(s.closesAt)}.`}
            </p>
          )}
        </div>
      )}
    </li>
  )
}
